// src/pages/AdminStaff.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Avatar,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Button,
} from "@mui/material";

import AdminNavbar, { AdminTopBar } from "../components/AdminNavbar";
import AdminIcon from "../img/AdminIcon.jpeg";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

import { collection, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { signOut } from "firebase/auth";
import { useAdminData } from "../context/AdminDataContext";
import { useTranslation } from "react-i18next";

function AdminAllAdmins() {
  const { admins = [], setAdmins, loading } = useAdminData();
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all"); // all | admin | staff | other

  // 1) Security: ensure current user is admin
  useEffect(() => {
    const verifyAdmin = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const adminQuery = query(
          collection(db, "admin"),
          where("uid", "==", user.uid)
        );

        const querySnapshot = await getDocs(adminQuery);

        if (querySnapshot.empty) {
          console.warn("User is not an admin. Signing out...");
          await signOut(auth);
        }
      } catch (err) {
        console.error("Error verifying admin access:", err);
        await signOut(auth);
      }
    };

    verifyAdmin();
  }, []);


  const filteredStaff = useMemo(() => {
    const q = (search || "").toLowerCase().trim();

    return (admins || [])
      .filter((p) => {
        const role = (p.role || "").toLowerCase();
        if (roleFilter === "all") return true;
        if (roleFilter === "admin") return role === "admin";
        if (roleFilter === "staff") return role === "staff";
        return role !== "admin" && role !== "staff";
      })
      .filter((p) => {
        if (!q) return true;
        const name = (p.fullName || "").toLowerCase();
        const email = (p.email || "").toLowerCase();
        const uid = (p.uid || "").toLowerCase();
        return name.includes(q) || email.includes(q) || uid.includes(q);
      });
  }, [admins, search, roleFilter]);

  const totalStaff = (admins || []).length;
  const totalAdmins = (admins || []).filter(
    (s) => (s.role || "").toLowerCase() === "admin"
  ).length;

  const getInitials = (fullName) => {
    const s = (fullName || "").trim();
    if (!s) return "A";
    const parts = s.split(" ").filter(Boolean);
    const a = parts[0]?.[0] || "A";
    const b = parts[1]?.[0] || "";
    return (a + b).toUpperCase();
  };

  return (
    <>
      <AdminTopBar />

      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          mt: "64px",
          backgroundColor: "#ffffff",
          flexDirection: "row-reverse",
          overflow: "hidden",
        }}
      >
        {/* RIGHT: Sidebar */}
        <AdminNavbar />

        {/* LEFT: Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1, md: 2 },
            overflowY: "auto",
            direction: "rtl",
          }}
        >
          {/* Header */}
          <Card
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: 4,
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            {/* RIGHT: Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: "#eef2ff",
                  color: "#4f46e5",
                  width: 46,
                  height: 46,
                  boxShadow: "0 8px 18px rgba(79,70,229,0.18)",
                }}
              >
                <AdminPanelSettingsIcon />
              </Avatar>

              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {t("فريق الإدارة")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("عرض وإدارة حسابات المشرفين والموظفين داخل المنصة")}
                </Typography>
              </Box>
            </Box>

            {/* LEFT: Quick stats */}
            <Box sx={{ display: "flex", gap: 1, direction: "ltr" }}>
              <Chip
                icon={<BadgeOutlinedIcon />}
                label={t("Total: {{count}}", { count: totalStaff })}
                sx={{
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.04)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  fontWeight: 700,
                }}
              />
              <Chip
                icon={<AdminPanelSettingsIcon />}
                label={t("Admins: {{count}}", { count: totalAdmins })}
                sx={{
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.04)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  fontWeight: 800,
                }}
              />
            </Box>
          </Card>

          {/* Controls row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2.5,
              flexWrap: "wrap",
            }}
          >
            {/* RIGHT: role filter (simple pills) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 0.75,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                border: "1px solid rgba(0,0,0,0.06)",
                width: "fit-content",
              }}
            >
              {[
                { key: "all", label: t("الكل") },
                { key: "admin", label: t("Admins") },
                { key: "staff", label: t("Staff") },
                { key: "other", label: t("Other") },
              ].map((opt) => (
                <Button
                  key={opt.key}
                  disableRipple
                  onClick={() => setRoleFilter(opt.key)}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontWeight: 800,
                    textTransform: "none",
                    backgroundColor: roleFilter === opt.key ? "#eef2ff" : "transparent",
                    color: roleFilter === opt.key ? "#4f46e5" : "text.secondary",
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </Box>

            {/* LEFT: search + filter icon */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", direction: "ltr" }}>
              <IconButton
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.12)",
                  backgroundColor: "#fff",
                  "&:hover": { backgroundColor: "#f9fafb" },
                }}
                // placeholder for future filter popover
              >
                <TuneIcon sx={{ color: "rgba(0,0,0,0.6)" }} />
              </IconButton>
              
              <TextField
                size="small"
                placeholder={t("Search by name / email / uid...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: 340,
                  backgroundColor: "#f9fafb",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* Cards grid (employee directory inspired) */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)", 
                },
                gap: 2,
                pb: 2,
              }}
            >
              {filteredStaff.map((p) => {
                const role = (p.role || "staff").toLowerCase();
                const roleLabel = role === "admin" ? t("ADMIN") : role.toUpperCase();

                return (
                  <Card
                    key={p.id}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(0,0,0,0.08)",
                      backgroundColor: "#fff",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                      p: 2,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {/* top row: avatar + menu */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        direction: "ltr",
                      }}
                    >
                      <IconButton
                        sx={{
                          borderRadius: 2,
                          backgroundColor: "#fff",
                          color: "#dc2626",
                          "&:hover": {
                            backgroundColor: "rgba(239,68,68,0.08)",
                            borderColor: "#dc2626",
                          },
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ direction: "rtl", textAlign: "right" }}>
                          <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                            {p.fullName || t("—")}
                          </Typography>
                        </Box>

                        <Avatar
                          src={AdminIcon}
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                          }}
                        />
                      </Box>
                    </Box>

                    {/* role chip */}
                    <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
                      <Chip
                        label={roleLabel}
                        size="small"
                        sx={{
                          borderRadius: "5px",
                          fontWeight: 900,
                          bgcolor: role === "admin" ? "rgba(34,197,94,0.12)" : "rgba(251,191,36,0.18)",
                          color: role === "admin" ? "rgba(22,163,74,0.95)" : "#b45309",
                          border: "1px solid rgba(0,0,0,0.06)",
                        }}
                      />
                    </Box>

                    {/* info rows */}
                    <Box sx={{ mt: 1.75, display: "grid", gap: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.5,
                          p: 1.25,
                          borderRadius: 2,
                          backgroundColor: "rgba(0,0,0,0.03)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          direction: "ltr",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <MailOutlineIcon sx={{ fontSize: 18, color: "rgba(0,0,0,0.55)" }} />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(0,0,0,0.75)",
                            fontWeight: 700,
                            direction: "ltr",
                            textAlign: "right",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 190,
                          }}
                          title={p.email || ""}
                        >
                          {p.email || t("—")}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.5,
                          p: 1.25,
                          borderRadius: 2,
                          backgroundColor: "rgba(0,0,0,0.03)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          direction: "ltr",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <BadgeOutlinedIcon sx={{ fontSize: 18, color: "rgba(0,0,0,0.55)" }} />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(0,0,0,0.75)",
                            fontWeight: 700,
                            direction: "ltr",
                            textAlign: "right",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 190,
                          }}
                          title={p.uid || ""}
                        >
                          {p.uid || t("—")}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

export default AdminAllAdmins;
